import React, { useState, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import './Checkout.css';

const CheckoutTransparente = ({ isOpen, onClose }) => {
  const { cartItems, getTotal } = useCart();
  const [mp, setMp] = useState(null);
  
  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    email: '',
    telefono: '',
    direccion: '',
    ciudad: '',
    departamento: '',
    codigoPostal: '',
    notas: '',
    // Datos de tarjeta
    cardNumber: '',
    cardholderName: '',
    cardExpirationMonth: '',
    cardExpirationYear: '',
    securityCode: '',
    identificationType: 'CI',
    identificationNumber: ''
  });

  const [errors, setErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (isOpen && window.MercadoPago) {
      // Inicializar MercadoPago con tu Public Key
      if (!process.env.REACT_APP_MERCADOPAGO_PUBLIC_KEY) {
        console.error('REACT_APP_MERCADOPAGO_PUBLIC_KEY no está configurada');
        return;
      }
      const mercadopago = new window.MercadoPago(process.env.REACT_APP_MERCADOPAGO_PUBLIC_KEY);
      setMp(mercadopago);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validar datos personales
    if (!formData.nombre.trim()) newErrors.nombre = 'El nombre es requerido';
    if (!formData.apellido.trim()) newErrors.apellido = 'El apellido es requerido';
    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }
    if (!formData.telefono.trim()) newErrors.telefono = 'El teléfono es requerido';
    if (!formData.direccion.trim()) newErrors.direccion = 'La dirección es requerida';
    if (!formData.ciudad.trim()) newErrors.ciudad = 'La ciudad es requerida';
    if (!formData.departamento.trim()) newErrors.departamento = 'El departamento es requerido';

    // Validar datos de tarjeta
    if (!formData.cardNumber.trim()) newErrors.cardNumber = 'Número de tarjeta requerido';
    if (!formData.cardholderName.trim()) newErrors.cardholderName = 'Nombre del titular requerido';
    if (!formData.cardExpirationMonth) newErrors.cardExpirationMonth = 'Mes de vencimiento requerido';
    if (!formData.cardExpirationYear) newErrors.cardExpirationYear = 'Año de vencimiento requerido';
    if (!formData.securityCode.trim()) newErrors.securityCode = 'CVV requerido';
    if (!formData.identificationNumber.trim()) newErrors.identificationNumber = 'Documento requerido';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsProcessing(true);

    try {
      // 1. Tokenizar la tarjeta con MercadoPago
      const cardToken = await mp.createCardToken({
        cardNumber: formData.cardNumber.replace(/\s/g, ''),
        cardholderName: formData.cardholderName,
        cardExpirationMonth: formData.cardExpirationMonth,
        cardExpirationYear: formData.cardExpirationYear,
        securityCode: formData.securityCode,
        identificationType: formData.identificationType,
        identificationNumber: formData.identificationNumber
      });

      if (cardToken.error) {
        throw new Error(cardToken.error.message);
      }

      // Detectar método de pago usando el SDK de MercadoPago
      const cardNumber = formData.cardNumber.replace(/\s/g, '');
      const bin = cardNumber.substring(0, 6);
      let paymentMethodId = 'visa';
      try {
        const pmResponse = await mp.getPaymentMethods({ bin });
        if (pmResponse.results && pmResponse.results.length > 0) {
          paymentMethodId = pmResponse.results[0].id;
        }
      } catch (e) {
        console.warn('No se pudo detectar el método de pago, usando fallback');
      }

      const orderData = {
        items: cartItems,
        cliente: {
          nombre: formData.nombre,
          apellido: formData.apellido,
          email: formData.email,
          telefono: formData.telefono,
          direccion: formData.direccion,
          ciudad: formData.ciudad,
          departamento: formData.departamento,
          codigoPostal: formData.codigoPostal,
          notas: formData.notas
        },
        total: getTotal(),
        token: cardToken.id,
        payment_method_id: paymentMethodId
      };

      const response = await fetch(`/api/process-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al procesar el pago');
      }

      const data = await response.json();

      // 3. Redirigir según el resultado
      if (data.status === 'approved') {
        window.location.href = '/success';
      } else if (data.status === 'pending') {
        window.location.href = '/pending';
      } else {
        window.location.href = '/failure';
      }

    } catch (error) {
      console.error('Error:', error);
      alert(`Error al procesar el pago: ${error.message}`);
      setIsProcessing(false);
    }
  };

  // Formatear número de tarjeta
  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || '';
    const parts = [];

    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }

    if (parts.length) {
      return parts.join(' ');
    } else {
      return value;
    }
  };

  const handleCardNumberChange = (e) => {
    const formatted = formatCardNumber(e.target.value);
    setFormData(prev => ({ ...prev, cardNumber: formatted }));
  };

  const months = Array.from({ length: 12 }, (_, i) => {
    const month = (i + 1).toString().padStart(2, '0');
    return { value: month, label: month };
  });

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 15 }, (_, i) => {
    const year = currentYear + i;
    return { value: year.toString(), label: year.toString() };
  });

  return (
    <div className="checkout-overlay" onClick={onClose}>
      <div className="checkout-container" onClick={(e) => e.stopPropagation()}>
        <button className="checkout-close" onClick={onClose}>✕</button>

        <div className="checkout-content">
          {/* Formulario */}
          <div className="checkout-form-section">
            <h2 className="checkout-title">FINALIZAR COMPRA</h2>
            <p className="checkout-subtitle">Completa tus datos para continuar</p>

            <form onSubmit={handleSubmit}>
              {/* Datos personales */}
              <div className="form-section">
                <h3 className="form-section-title">Datos Personales</h3>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Nombre *</label>
                    <input
                      type="text"
                      name="nombre"
                      value={formData.nombre}
                      onChange={handleChange}
                      className={errors.nombre ? 'error' : ''}
                    />
                    {errors.nombre && <span className="error-message">{errors.nombre}</span>}
                  </div>

                  <div className="form-group">
                    <label>Apellido *</label>
                    <input
                      type="text"
                      name="apellido"
                      value={formData.apellido}
                      onChange={handleChange}
                      className={errors.apellido ? 'error' : ''}
                    />
                    {errors.apellido && <span className="error-message">{errors.apellido}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={errors.email ? 'error' : ''}
                    />
                    {errors.email && <span className="error-message">{errors.email}</span>}
                  </div>

                  <div className="form-group">
                    <label>Teléfono *</label>
                    <input
                      type="tel"
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleChange}
                      className={errors.telefono ? 'error' : ''}
                    />
                    {errors.telefono && <span className="error-message">{errors.telefono}</span>}
                  </div>
                </div>
              </div>

              {/* Dirección de envío */}
              <div className="form-section">
                <h3 className="form-section-title">Dirección de Envío</h3>
                
                <div className="form-group">
                  <label>Dirección *</label>
                  <input
                    type="text"
                    name="direccion"
                    value={formData.direccion}
                    onChange={handleChange}
                    className={errors.direccion ? 'error' : ''}
                  />
                  {errors.direccion && <span className="error-message">{errors.direccion}</span>}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Ciudad *</label>
                    <input
                      type="text"
                      name="ciudad"
                      value={formData.ciudad}
                      onChange={handleChange}
                      className={errors.ciudad ? 'error' : ''}
                    />
                    {errors.ciudad && <span className="error-message">{errors.ciudad}</span>}
                  </div>

                  <div className="form-group">
                    <label>Departamento *</label>
                    <input
                      type="text"
                      name="departamento"
                      value={formData.departamento}
                      onChange={handleChange}
                      className={errors.departamento ? 'error' : ''}
                    />
                    {errors.departamento && <span className="error-message">{errors.departamento}</span>}
                  </div>

                  <div className="form-group">
                    <label>Código Postal</label>
                    <input
                      type="text"
                      name="codigoPostal"
                      value={formData.codigoPostal}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>

              {/* Datos de tarjeta */}
              <div className="form-section">
                <h3 className="form-section-title">Datos de Pago</h3>
                
                <div className="form-group">
                  <label>Número de Tarjeta *</label>
                  <input
                    type="text"
                    name="cardNumber"
                    value={formData.cardNumber}
                    onChange={handleCardNumberChange}
                    placeholder="1234 5678 9012 3456"
                    maxLength="19"
                    className={errors.cardNumber ? 'error' : ''}
                  />
                  {errors.cardNumber && <span className="error-message">{errors.cardNumber}</span>}
                </div>

                <div className="form-group">
                  <label>Nombre del Titular *</label>
                  <input
                    type="text"
                    name="cardholderName"
                    value={formData.cardholderName}
                    onChange={handleChange}
                    placeholder="Como aparece en la tarjeta"
                    className={errors.cardholderName ? 'error' : ''}
                  />
                  {errors.cardholderName && <span className="error-message">{errors.cardholderName}</span>}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Vencimiento *</label>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <select
                        name="cardExpirationMonth"
                        value={formData.cardExpirationMonth}
                        onChange={handleChange}
                        className={errors.cardExpirationMonth ? 'error' : ''}
                      >
                        <option value="">Mes</option>
                        {months.map(m => (
                          <option key={m.value} value={m.value}>{m.label}</option>
                        ))}
                      </select>
                      <select
                        name="cardExpirationYear"
                        value={formData.cardExpirationYear}
                        onChange={handleChange}
                        className={errors.cardExpirationYear ? 'error' : ''}
                      >
                        <option value="">Año</option>
                        {years.map(y => (
                          <option key={y.value} value={y.value}>{y.label}</option>
                        ))}
                      </select>
                    </div>
                    {(errors.cardExpirationMonth || errors.cardExpirationYear) && 
                      <span className="error-message">Vencimiento requerido</span>
                    }
                  </div>

                  <div className="form-group">
                    <label>CVV *</label>
                    <input
                      type="text"
                      name="securityCode"
                      value={formData.securityCode}
                      onChange={handleChange}
                      placeholder="123"
                      maxLength="4"
                      className={errors.securityCode ? 'error' : ''}
                    />
                    {errors.securityCode && <span className="error-message">{errors.securityCode}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Tipo de Documento *</label>
                    <select
                      name="identificationType"
                      value={formData.identificationType}
                      onChange={handleChange}
                    >
                      <option value="CI">Cédula de Identidad</option>
                      <option value="RUT">RUT</option>
                      <option value="Otro">Otro</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Número de Documento *</label>
                    <input
                      type="text"
                      name="identificationNumber"
                      value={formData.identificationNumber}
                      onChange={handleChange}
                      className={errors.identificationNumber ? 'error' : ''}
                    />
                    {errors.identificationNumber && <span className="error-message">{errors.identificationNumber}</span>}
                  </div>
                </div>
              </div>

              {/* Notas */}
              <div className="form-section">
                <h3 className="form-section-title">Notas Adicionales</h3>
                <div className="form-group">
                  <label>Notas del pedido (opcional)</label>
                  <textarea
                    name="notas"
                    value={formData.notas}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Instrucciones especiales para la entrega..."
                  />
                </div>
              </div>

              <button
                type="submit"
                className="checkout-submit-btn"
                disabled={isProcessing}
              >
                {isProcessing ? 'PROCESANDO...' : `PAGAR $${getTotal()}`}
              </button>
            </form>
          </div>

          {/* Resumen */}
          <div className="checkout-summary-section">
            <h3 className="summary-title">Resumen del Pedido</h3>
            
            <div className="summary-items">
              {cartItems.map(item => (
                <div key={item.id} className="summary-item">
                  <img src={item.imagen} alt={item.nombre} />
                  <div className="summary-item-info">
                    <p className="summary-item-name">{item.nombre}</p>
                    <p className="summary-item-quantity">Cantidad: {item.cantidad}</p>
                  </div>
                  <p className="summary-item-price">${item.precio * item.cantidad}</p>
                </div>
              ))}
            </div>

            <div className="summary-total">
              <span>TOTAL</span>
              <span className="total-amount">${getTotal()}</span>
            </div>

            <div className="security-badge">
              <p>🔒 Pago seguro procesado por MercadoPago</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutTransparente;
